/**
 * This component will show the booking info and calculated total price.
 * I.e. dates and other details related to payment decision in receipt format.
 */
import React from 'react';
import { oneOf, string } from 'prop-types';
import classNames from 'classnames';

import { FormattedMessage, intlShape, injectIntl } from '../../util/reactIntl';
import {
  propTypes,
  LISTING_UNIT_TYPES,
  LINE_ITEM_CUSTOMER_COMMISSION,
  LINE_ITEM_PROVIDER_COMMISSION,
  APPLICABLE_FEE_TYPES,
  LINE_ITEM_DELIVERY_FEE,
  LINE_ITEM_PICKUP_FEE,
} from '../../util/types';
import { getLineItemFeeCode } from '../../util/lineItems';

import LineItemBookingPeriod from './LineItemBookingPeriod';
import LineItemBasePriceMaybe from './LineItemBasePriceMaybe';
import LineItemSubTotalMaybe from './LineItemSubTotalMaybe';
import LineItemShippingFeeMaybe from './LineItemShippingFeeMaybe';
import LineItemPickupFeeMaybe from './LineItemPickupFeeMaybe';
import LineItemCustomerCommissionMaybe from './LineItemCustomerCommissionMaybe';
import LineItemCustomerCommissionRefundMaybe from './LineItemCustomerCommissionRefundMaybe';
import LineItemProviderCommissionMaybe from './LineItemProviderCommissionMaybe';
import LineItemProviderCommissionRefundMaybe from './LineItemProviderCommissionRefundMaybe';
import LineItemRefundMaybe from './LineItemRefundMaybe';
import LineItemTotalPrice from './LineItemTotalPrice';

import { convertLineItems, formatMoney, unitDivisor } from '../../util/currency';
import { useSelector } from 'react-redux';
import { types as sdkTypes } from '../../util/sdkLoader';
import LineItemAdditionalFees from './LineItemAdditionalFees';
import BookingSupport from '../BookingSupport/BookingSupport';
import { H6 } from '../Heading/Heading';
import css from './OrderBreakdown.module.css';
import IconCollection from '../IconCollection/IconCollection';
import ApplicableFee from './ApplicableFee';

const { Money } = sdkTypes;

const getValidCode = (code) => {
  const newCode = code.includes(':');
  const updatedCode = !newCode ? code : `line-item/${code.split('-').reverse()[0]}`
  return updatedCode;
};

const getValidLineItemCode = (lineItem) => {
  const newCode = lineItem.code.includes(':');
  const code = !newCode ? lineItem.code : `line-item/${lineItem.code.split('-').reverse()[0]}`
  return code;
}

// Replaces unitPrice, lineTotal, and quantity on each Sharetribe line item with
// values from transaction metadata when available. Metadata stores amounts in
// major units (e.g. 6.67) while Sharetribe Money objects use subunits (e.g. 667),
// so we multiply back by the currency's subunit divisor before wrapping in Money.
// This ensures checkout and order-detail pages show the same rounded per-day
// breakdown as the listing page — e.g. "€6.67 × 6 days = €40.00" for a capped
// weekly rate — regardless of what Sharetribe stored.
const patchLineItemsWithMetadata = (lineItems, metadataLineItems) => {
  if (!metadataLineItems?.length) return lineItems;
  return lineItems.map(item => {
    if (item.reversal) return item;
    const normalizedCode = getValidCode(item.code);
    // Extract the 1-based item number from the Sharetribe line item code
    // (e.g. 'line-item/item-1:-day' → '1'). Metadata carries an itemIndex field
    // (also 1-based) so each cart item is matched uniquely. Falls back to
    // normalized-code matching for old transactions without itemIndex.
    const txItemNum = item.code.match(/item-(\d+)/)?.[1];
    const metaItem = metadataLineItems.find(m => {
      if (m.reversal) return false;
      if (txItemNum != null && m.itemIndex != null) return String(m.itemIndex) === txItemNum;
      return m.code === normalizedCode;
    });
    if (!metaItem?.unitPrice || !metaItem?.lineTotal) return item;
    try {
      const upCurrency = metaItem.unitPrice.currency;
      const ltCurrency = metaItem.lineTotal.currency || upCurrency;
      return {
        ...item,
        unitPrice: new Money(Math.round(metaItem.unitPrice.amount * unitDivisor(upCurrency)), upCurrency),
        lineTotal: new Money(Math.round(metaItem.lineTotal.amount * unitDivisor(ltCurrency)), ltCurrency),
        quantity: metaItem.quantity != null ? metaItem.quantity : item.quantity,
        units: metaItem.units != null ? metaItem.units : item.units,
        seats: metaItem.seats != null ? metaItem.seats : item.seats,
      };
    } catch (_) {
      return item;
    }
  });
};

const PickupReturnOptions = ({ transaction, code, intl, currentUserCurrency }) => {
  const lineItemsFromMetaData = transaction.attributes?.metadata?.lineItems;
  const txLineItems = transaction?.attributes?.lineItems || [];

  // If a delivery/pickup fee is charged, the owner is handling that leg and the
  // paid fee line item already appears in the breakdown. Showing "Pickup at Owner's
  // Location: €0.00" alongside a "Delivery by Owner: €8.51" charge is contradictory
  // and confusing, so suppress the €0.00 rows whenever the matching fee is present.
  const hasDeliveryFee = txLineItems.some(
    li => getLineItemFeeCode(li) === LINE_ITEM_DELIVERY_FEE && !li.reversal
  );
  const hasPickupFee = txLineItems.some(
    li => getLineItemFeeCode(li) === LINE_ITEM_PICKUP_FEE && !li.reversal
  );

  const updatedCode = getValidCode(code);
  const item = lineItemsFromMetaData?.find(item => item.code === updatedCode)
    ?? transaction?.attributes?.lineItems?.find(item => item.code === code);

  const { pickupAtOwnerLocation, returnAtOwnerLocation } = item || {};

  const showPickupAtOwner = pickupAtOwnerLocation && !hasDeliveryFee;
  const showReturnAtOwner = returnAtOwnerLocation && !hasPickupFee;

  if (!showPickupAtOwner && !showReturnAtOwner) {
    return null;
  }

  const zeroMoney = new Money(0, currentUserCurrency);
  const formattedZero = formatMoney(intl, zeroMoney);

  return (
    <>
      {showPickupAtOwner && (
        <div className={css.lineItem}>
          <span className={css.itemLabel}>
            <FormattedMessage id="OrderBreakdown.pickupAtOwnerLocation" />
          </span>
          <span className={css.itemValue}>{formattedZero}</span>
        </div>
      )}
      {showReturnAtOwner && (
        <div className={css.lineItem}>
          <span className={css.itemLabel}>
            <FormattedMessage id="OrderBreakdown.returnAtOwnerLocation" />
          </span>
          <span className={css.itemValue}>{formattedZero}</span>
        </div>
      )}
    </>
  );
};

export const OrderBreakdownComponent = props => {
  const {
    rootClassName,
    className,
    userRole,
    transaction,
    booking,
    intl,
    dateType,
    timeZone,
    currency,
    marketplaceName,
    checkoutButton,
    showBookingSupport,
    lineItemClass,
    lineItemTotalClass,
    lineItemsContainerClassName,
    breakdownTitle,
    isShowCommissionLineItem = true,
    savingPerDayMessage,
    showSecurely,
    itemThumbnails,
  } = props;

  const { currentUserCurrency } = useSelector(state => state.user);
  const { exchangeRates } = useSelector(state => state.exchangeRate);

  if (exchangeRates.length === 0) {
    return null;
  };

  const isCustomer = userRole === 'customer';
  const isProvider = userRole === 'provider';
  const allLineItems = transaction.attributes.lineItems || [];
  const metadataLineItems = transaction.attributes?.metadata?.lineItems;

  // Patch Sharetribe's line items with server-computed display values stored in
  // metadata. For capped/floored multi-day packages the server stores the correct
  // per-day unit price and exact package total in metadata; Sharetribe's own stored
  // line items may have truncated or collapsed values (quantity=1) after the fix.
  const patchedLineItems = patchLineItemsWithMetadata(allLineItems, metadataLineItems);

  // We'll show only line-items that are specific for the current userRole (customer vs provider)
  const lineItems = patchedLineItems.filter(lineItem => lineItem.includeFor?.includes(userRole));

  // replace static exchange rates with the real one.
  const convertedLineItems = convertLineItems(exchangeRates, lineItems, currentUserCurrency);

  const unitLineItems = convertedLineItems.filter(item => {
    // we are passing new code as 'line-item/item-1:title:unit
    // We don't want to break old code 'line-item/unit'
    const code = getValidLineItemCode(item)
    return LISTING_UNIT_TYPES.includes(code) && !item.reversal
  }
  );

  const applicableFeeLineItems = convertedLineItems.filter(item => {
    const fee = item.code.split('/').pop();
    return APPLICABLE_FEE_TYPES.includes(`line-item/${fee}`) && !item.reversal;
  }).map(item => {
    // Enrich with listing title from transaction metadata if not present
    if (!item.listingTitle) {
      const lineItemsFromMetaData = transaction.attributes?.metadata?.lineItems;

      // First, try to find exact match by code in metadata
      const metadataItem = lineItemsFromMetaData?.find(metaItem => {
        return metaItem.code === item.code || getValidCode(metaItem.code) === getValidLineItemCode(item);
      });

      if (metadataItem?.listingTitle) {
        return { ...item, listingTitle: metadataItem.listingTitle };
      }
    }
    return item;
  });

  // console.log('applicableFeeLineItems-->', applicableFeeLineItems);

  // Line-item code that matches with base unit: day, night, hour, item
  const lineItemUnitType = unitLineItems?.[0]?.code;

  const hasCommissionLineItem = convertedLineItems.find(item => {
    const hasCustomerCommission = isCustomer && item.code === LINE_ITEM_CUSTOMER_COMMISSION;
    const hasProviderCommission = isProvider && item.code === LINE_ITEM_PROVIDER_COMMISSION;
    return (hasCustomerCommission || hasProviderCommission) && !item.reversal;
  });

  const classes = classNames(rootClassName || css.root, className);

  /**
   * OrderBreakdown contains different line items:
   *
   * LineItemBookingPeriod: prints booking start and booking end types. Prop dateType
   * determines if the date and time or only the date is shown
   *
   * LineItemShippingFeeMaybe: prints the shipping fee (combining additional fee of
   * additional items into it).
   *
   * LineItemShippingFeeRefundMaybe: prints the amount of refunded shipping fee
   *
   * LineItemBasePriceMaybe: prints the base price calculation for the listing, e.g.
   * "$150.00 * 2 nights $300"
   *
   * LineItemUnknownItemsMaybe: prints the line items that are unknown. In ideal case there
   * should not be unknown line items. If you are using custom pricing, you should create
   * new custom line items if you need them.
   *
   * LineItemSubTotalMaybe: prints subtotal of line items before possible
   * commission or refunds
   *
   * LineItemRefundMaybe: prints the amount of refund
   *
   * LineItemCustomerCommissionMaybe: prints the amount of customer commission
   * The default transaction process used by this template doesn't include the customer commission.
   *
   * LineItemCustomerCommissionRefundMaybe: prints the amount of refunded customer commission
   *
   * LineItemProviderCommissionMaybe: prints the amount of provider commission
   *
   * LineItemProviderCommissionRefundMaybe: prints the amount of refunded provider commission
   *
   * LineItemTotalPrice: prints total price of the transaction
   *
   */

  return (
    <div className={classes}>
      <div className={classNames(css.lineItemsContainer, lineItemsContainerClassName)}>
        {breakdownTitle && <H6 as="h3" className={css.bookingBreakdownTitle}>
          {breakdownTitle}
        </H6>}
        {savingPerDayMessage && <span className={css.savingPerDayMessage}>{savingPerDayMessage}</span>}
        {unitLineItems.map(i => {
          const lineItemUnitType = i.code;
          const idxMatch = lineItemUnitType.match(/item-(\d+)/);
          const thumbUrl = idxMatch && itemThumbnails ? itemThumbnails[parseInt(idxMatch[1]) - 1] || null : null;
          return (
            <div key={lineItemUnitType} >
              <LineItemBasePriceMaybe lineItems={convertedLineItems} code={lineItemUnitType} intl={intl} lineItemClass={lineItemClass} itemThumbnailUrl={thumbUrl} />
              <PickupReturnOptions
                transaction={transaction}
                code={lineItemUnitType}
                intl={intl}
                currentUserCurrency={currentUserCurrency}
              />
              <LineItemBookingPeriod
                booking={booking}
                code={lineItemUnitType}
                dateType={dateType}
                timeZone={timeZone}
                transaction={transaction}
                lineItemClass={lineItemClass}
              />
            </div>
          )
        })}

        {applicableFeeLineItems.map(item => {
          return <ApplicableFee key={item.code} item={item} intl={intl} lineItemClass={lineItemClass} />
        })}


        <LineItemSubTotalMaybe
          lineItems={convertedLineItems}
          code={lineItemUnitType}
          userRole={userRole}
          intl={intl}
          marketplaceCurrency={currency}
          lineItemClass={lineItemClass}
        />
        <LineItemRefundMaybe lineItems={convertedLineItems} intl={intl} marketplaceCurrency={currency} lineItemClass={lineItemClass} />

        <LineItemCustomerCommissionMaybe
          lineItems={convertedLineItems}
          isCustomer={isCustomer}
          marketplaceName={marketplaceName}
          intl={intl}
          lineItemClass={lineItemClass}
        />
        <LineItemCustomerCommissionRefundMaybe
          lineItems={convertedLineItems}
          isCustomer={isCustomer}
          marketplaceName={marketplaceName}
          intl={intl}
          lineItemClass={lineItemClass}
        />

        <LineItemProviderCommissionMaybe
          lineItems={convertedLineItems}
          isProvider={isProvider}
          marketplaceName={marketplaceName}
          intl={intl}
          lineItemClass={lineItemClass}
        />
        <LineItemProviderCommissionRefundMaybe
          lineItems={convertedLineItems}
          isProvider={isProvider}
          marketplaceName={marketplaceName}
          intl={intl}
          lineItemClass={lineItemClass}
        />

        <LineItemTotalPrice transaction={transaction} isProvider={isProvider} intl={intl} lineItemClass={lineItemClass} lineItemTotalClass={lineItemTotalClass} exchangeRates={exchangeRates} currentUserCurrency={currentUserCurrency} />

        {checkoutButton && (<div className={css.checkoutButton}>
          {checkoutButton}
        </div>)
        }
        {showSecurely && <div className={css.processedSecurely}><FormattedMessage id='OrderBreakdown.processedSecurely' /><IconCollection name="processedSecurely" /></div>}
      </div>

      {showBookingSupport && <BookingSupport />}

    </div>
  );
};

OrderBreakdownComponent.defaultProps = {
  rootClassName: null,
  className: null,
  booking: null,
  dateType: null,
};

OrderBreakdownComponent.propTypes = {
  rootClassName: string,
  className: string,

  marketplaceName: string.isRequired,
  userRole: oneOf(['customer', 'provider']).isRequired,
  transaction: propTypes.transaction.isRequired,
  booking: propTypes.booking,
  dateType: propTypes.dateType,

  // from injectIntl
  intl: intlShape.isRequired,
};

const OrderBreakdown = injectIntl(OrderBreakdownComponent);

OrderBreakdown.displayName = 'OrderBreakdown';

export default OrderBreakdown;
